import { PUBLIC_TOKEN_PREFIX, type PublicTemplate } from '@signa/shared';

/**
 * 公開 API の取得。
 *
 * `GET /pub/v1/templates/{publicToken}`([#30](../../../../docs/issues/30-api-spec/DESIGN.md))。
 * **認証しない**(トークンの知識のみ — ADR-0015)。
 */

/** ビューアが表示に使うエラー種別([#33](../../../../docs/issues/33-viewer-embed/DESIGN.md))。 */
export const VIEWER_ERROR_CODES = {
  /** 公開 API が 404。**公開 OFF / 削除済み / トークン無効を区別しない。** */
  NOT_AVAILABLE: 'NOT_AVAILABLE',
  /** トークン属性が空・形式不正(埋め込みミス)。 */
  INVALID_TOKEN: 'INVALID_TOKEN',
  /** 通信失敗・5xx。 */
  NETWORK: 'NETWORK',
  /**
   * 公開 API が 403。**このドメインが許可リストに無い**
   * ([ADR-0027](../../../../docs/adr/0027-public-api-origin-allowlist.md))。
   *
   * **`NOT_AVAILABLE` と分ける。** 「利用できません」と出すと埋め込み先が
   * トークンを疑うが、実際に必要なのは**ドメインの登録**である。
   */
  ORIGIN_NOT_ALLOWED: 'ORIGIN_NOT_ALLOWED',
  /** 背景画像が取得できない(変換中の可能性)。 */
  BACKGROUND_PENDING: 'BACKGROUND_PENDING',
  /** WebGL2 非対応。 */
  UNSUPPORTED: 'UNSUPPORTED',
  /** 動画の形式を再生できない。 */
  VIDEO_UNSUPPORTED: 'VIDEO_UNSUPPORTED',
  /** 描画の初期化に失敗(復帰不能)。 */
  RENDER_FAILED: 'RENDER_FAILED',
} as const;

export type ViewerErrorCode = (typeof VIEWER_ERROR_CODES)[keyof typeof VIEWER_ERROR_CODES];

export class ViewerError extends Error {
  constructor(readonly code: ViewerErrorCode) {
    super(code);
    this.name = 'ViewerError';
  }
}

/**
 * 公開トークンの形式検査。
 *
 * `pv_` + base64url(#30 / ADR-0015)。**サーバに投げる前に弾く** ——
 * 埋め込みミスを 404 と区別して報告できる(#33 の `INVALID_TOKEN`)。
 */
export function isValidToken(token: string | null | undefined): token is string {
  if (typeof token !== 'string') return false;
  if (!token.startsWith(PUBLIC_TOKEN_PREFIX)) return false;
  const body = token.slice(PUBLIC_TOKEN_PREFIX.length);
  // 128 bit を base64url で表すと 22 文字。将来伸びる余地を持たせて範囲で見る。
  return /^[A-Za-z0-9_-]{16,64}$/.test(body);
}

/** 公開 API のベース URL を配布元から導く。 */
export function defaultApiBase(scriptSrc: string | null): string {
  if (scriptSrc === null) return '';
  try {
    return new URL(scriptSrc).origin;
  } catch {
    return '';
  }
}

/**
 * テンプレートを取得する。
 *
 * **同一トークンの取得は 1 回にまとめる**(#33)。1 ページに同じトークンの
 * 枠が複数あっても、同じデータを複数回取る理由がない。
 */
const inflight = new Map<string, Promise<PublicTemplate>>();

export function fetchTemplate(apiBase: string, token: string): Promise<PublicTemplate> {
  const url = templateUrl(apiBase, token);

  const existing = inflight.get(url);
  if (existing !== undefined) return existing;

  const request = requestTemplate(url).finally(() => {
    // 完了したら共有をやめる。**再試行が古い結果を掴まないため。**
    inflight.delete(url);
  });
  inflight.set(url, request);
  return request;
}

export function templateUrl(apiBase: string, token: string): string {
  return `${apiBase}/pub/v1/templates/${encodeURIComponent(token)}`;
}

async function requestTemplate(url: string): Promise<PublicTemplate> {
  let response: Response;
  try {
    // 認証情報を送らない(公開 API は認証しない — ADR-0015)。
    response = await fetch(url, { credentials: 'omit', mode: 'cors' });
  } catch {
    throw new ViewerError(VIEWER_ERROR_CODES.NETWORK);
  }

  // **404 は理由を区別しない**(#30 の配慮を無意味にしないため)。
  if (response.status === 404) throw new ViewerError(VIEWER_ERROR_CODES.NOT_AVAILABLE);

  /*
   * **403 は「ドメインの登録漏れ」。**
   *
   * 埋め込み先の開発者に**コンソールで理由を伝える** —— 閲覧者向けの
   * 表示だけでは、何を直せばよいか分からない(#33 の `INVALID_TOKEN` と
   * 同じ方針)。API 側が 403 にもオリジンを反射しているので、
   * **ここまで応答が届く**。
   */
  if (response.status === 403) {
    console.warn(
      '[SignaPreview] このドメインは許可されていません。' +
        '表示するには、提供元にドメインの登録を依頼してください。',
    );
    throw new ViewerError(VIEWER_ERROR_CODES.ORIGIN_NOT_ALLOWED);
  }

  if (!response.ok) throw new ViewerError(VIEWER_ERROR_CODES.NETWORK);

  try {
    return (await response.json()) as PublicTemplate;
  } catch {
    throw new ViewerError(VIEWER_ERROR_CODES.NETWORK);
  }
}

/**
 * 背景画像が取得できるか確かめる。
 *
 * **変換中は 404 を返す時間帯がある**(#33 が見つけた論点)。#61 で
 * `background` が nullable になり、変換未完了なら `null` が返るように
 * なったが、**`null` でなくても実際の取得が失敗しうる**(CDN の伝播待ち)。
 */
export function loadImage(url: string): Promise<boolean> {
  return new Promise<boolean>((resolve) => {
    const image = new Image();
    image.onload = (): void => {
      resolve(true);
    };
    image.onerror = (): void => {
      resolve(false);
    };
    image.src = url;
  });
}

/**
 * 背景画像の再試行スケジュール(ミリ秒)。
 *
 * **指数バックオフで最大 5 回・上限およそ 30 秒**(#33)。それを超えたら
 * `NETWORK` に落として手動再試行へ切り替える。
 */
export const BACKGROUND_RETRY_DELAYS: readonly number[] = [1000, 2000, 4000, 8000, 15000];

/** 再試行の総待ち時間(上限の確認用)。 */
export function totalRetryDelay(): number {
  return BACKGROUND_RETRY_DELAYS.reduce((sum, d) => sum + d, 0);
}
